import { useTranslation } from "next-i18next";
import { MultiPurposeParagraph } from "@/components/Atoms/Paragraph";

export interface ContactInfoCardProps {
	entry: {
		id: string;
		title?: string;
		isEContacts?: boolean;
		isPhysicalAdress?: boolean;
		isSchedule?: boolean;
		email?: string;
		googleMapsLocation?: string;
		address?: string;
		mobile?: string;
		telephone?: string;
		timeFrames?: string;
		icon?: {
			url: string;
			alt?: string;
		};
	};
	textColor: any;
	titleColor: any;
	titleType: "default" | "highlight";
	iconAlign?: "default" | "row";
}

// Helper to check if a string has actual content
const hasContent = (value: string | undefined | null): value is string => {
	return typeof value === "string" && value.trim().length > 0;
};

export function ContactInfoCard({
	entry,
	textColor,
	titleColor,
	titleType,
	iconAlign = "default",
}: ContactInfoCardProps) {
	const { t } = useTranslation();

	// Strict content checks
	const hasAddress = hasContent(entry.address);
	const hasEmail = hasContent(entry.email);
	const hasTelephone = hasContent(entry.telephone);
	const hasMobile = hasContent(entry.mobile);
	const hasTimeFrames = hasContent(entry.timeFrames);
	const hasTitle = hasContent(entry.title);

	const hasContacts = hasEmail || hasTelephone || hasMobile;
	const hasAnyContent = hasAddress || hasContacts || hasTimeFrames;

	// Style for links - underline color matches text color
	const linkStyle = {
		textDecorationColor: textColor?.color?.hex,
	} as React.CSSProperties;

	// Icon component - smaller size to match TextWithImageRecord visual appearance
	const IconElement = entry.icon?.url && (
		<div className="w-8 h-8 lg:w-10 lg:h-10 relative flex-shrink-0">
			<img
				src={entry.icon.url}
				alt={entry.icon.alt || ""}
				className="w-full h-full object-contain"
				aria-hidden={!entry.icon.alt}
			/>
		</div>
	);

	// Title component
	const TitleElement = hasTitle && (
		<MultiPurposeParagraph
			type={titleType === "default" ? "headline" : "headlineFeatured"}
			data={entry.title!}
			color={titleColor?.color}
		/>
	);

	return (
		<article
			className="flex flex-col gap-4 justify-start items-start text-left"
			aria-label={entry.title || t("contact")}
		>
			{/* Icon and Title - row or default layout */}
			{iconAlign === "row" ? (
				<div className="flex flex-row items-center justify-start gap-4">
					{IconElement}
					{TitleElement}
				</div>
			) : (
				<>
					{IconElement}
					{TitleElement}
				</>
			)}

			{/* Contact Information */}
			{hasAnyContent && (
				<address className="flex flex-col gap-3 not-italic w-full">
					{/* Physical Address - with Google Maps link if available */}
					{hasAddress && (
						<div className="flex flex-col gap-1">
							{hasContent(entry.googleMapsLocation) ? (
								<a
									href={entry.googleMapsLocation!}
									target="_blank"
									rel="noopener noreferrer"
									style={linkStyle}
									className="hover:underline transition-all duration-200"
									aria-label={`${t("viewOnMap")}: ${entry.address}`}
								>
									<MultiPurposeParagraph
										type="default"
										data={entry.address!}
										color={textColor?.color}
									/>
								</a>
							) : (
								<MultiPurposeParagraph
									type="default"
									data={entry.address!}
									color={textColor?.color}
								/>
							)}
						</div>
					)}

					{/* Electronic Contacts - Email with mailto:, Phone with tel: */}
					{hasContacts && (
						<div className="flex flex-col gap-2">
							{/* Email - mailto: link */}
							{hasEmail && (
								<a
									href={`mailto:${entry.email}`}
									style={linkStyle}
									className="hover:underline transition-all duration-200 inline-flex items-center gap-2"
									aria-label={`${t("email")}: ${entry.email}`}
								>
									<MultiPurposeParagraph
										type="default"
										data={entry.email!}
										color={textColor?.color}
									/>
								</a>
							)}

							{/* Telephone - tel: link */}
							{hasTelephone && (
								<a
									href={`tel:${entry.telephone!.replace(/\s/g, "")}`}
									style={linkStyle}
									className="hover:underline transition-all duration-200 inline-flex items-center gap-2"
									aria-label={`${t("phone")}: ${entry.telephone}`}
								>
									<MultiPurposeParagraph
										type="default"
										data={entry.telephone!}
										color={textColor?.color}
									/>
								</a>
							)}

							{/* Mobile - tel: link */}
							{hasMobile && (
								<a
									href={`tel:${entry.mobile!.replace(/\s/g, "")}`}
									style={linkStyle}
									className="hover:underline transition-all duration-200 inline-flex items-center gap-2"
									aria-label={`${t("mobile")}: ${entry.mobile}`}
								>
									<MultiPurposeParagraph
										type="default"
										data={entry.mobile!}
										color={textColor?.color}
									/>
								</a>
							)}
						</div>
					)}

					{/* Schedule / Time Frames */}
					{hasTimeFrames && (
						<div className="flex flex-col gap-1">
							<MultiPurposeParagraph
								type="default"
								data={entry.timeFrames!}
								color={textColor?.color}
							/>
						</div>
					)}
				</address>
			)}
		</article>
	);
}
